import { HStack, Select, Text, useColorModeValue } from '@chakra-ui/react'
import { Bot } from 'lucide-react'
import React from 'react'
import { useAnalyticsAgents } from '../../hooks/analytics/useAnalytics'
import { AgentFilter } from '../../types/analyticsTypes'

const ALL_AGENTS = ''

interface AgentFilterSelectProps {
  value: AgentFilter
  onChange: (agentId: AgentFilter) => void
}

/**
 * Dashboard-wide agent filter. Every widget on the page is scoped to the
 * selected agent's conversations; the empty option means all agents. The
 * control is highlighted while an agent is selected so a scoped dashboard is
 * never mistaken for the all-agents view.
 */
const AgentFilterSelect: React.FC<AgentFilterSelectProps> = ({ value, onChange }) => {
  const { data: agents = [], isLoading } = useAnalyticsAgents()
  const isScoped = value !== null
  const scopedBg = useColorModeValue('blue.50', 'blue.900')
  const scopedBorder = useColorModeValue('blue.400', 'blue.300')
  const scopedText = useColorModeValue('blue.700', 'blue.200')

  return (
    <HStack
      spacing={3}
      px={3}
      py={1.5}
      borderRadius='md'
      borderWidth='1px'
      borderColor={isScoped ? scopedBorder : 'app.border'}
      bg={isScoped ? scopedBg : 'app.bg.page'}
    >
      <HStack spacing={1.5} color={isScoped ? scopedText : 'app.text.secondary'}>
        <Bot size={18} />
        <Text fontSize='sm' fontWeight='semibold' whiteSpace='nowrap'>
          Agent
        </Text>
      </HStack>
      <Select
        size='md'
        width='auto'
        minW='200px'
        maxW='320px'
        bg='app.bg'
        fontWeight={isScoped ? 'semibold' : 'normal'}
        borderColor={isScoped ? scopedBorder : 'app.border'}
        aria-label='Filter by agent'
        value={value ?? ALL_AGENTS}
        isDisabled={isLoading}
        onChange={event => onChange(event.target.value === ALL_AGENTS ? null : event.target.value)}
      >
        <option value={ALL_AGENTS}>All agents</option>
        {agents.map(agent => (
          <option key={agent.id} value={agent.id}>
            {agent.name}
          </option>
        ))}
      </Select>
    </HStack>
  )
}

export default AgentFilterSelect
